/** The one `ExtractionKind` the vaccination-stamp scan flow uses — mirrors
 * `HORSE_SCAN_KINDS` in `horse-scan-extraction.ts`. */
export const VACCINATION_SCAN_KINDS = ["VACCINATION_STAMP"];
export function isVaccinationScanKind(kind) {
    return VACCINATION_SCAN_KINDS.includes(kind);
}
/**
 * Every field the vaccination-stamp scan can prefill
 * (`docs/products/stable.md` "Vaccinations": "extraction returns vaccine
 * type, date administered, batch/serial, and administering vet"). Unlike
 * Horse Details' scan, "administering vet" here is never resolved to a
 * Horseman account — the system already knows who is doing the logging
 * (`administeringHorsemanId` on the record is always the actor, "mirroring
 * Vet Records' actor-is-the-logger convention", `COMPANY.md`); the OCR'd
 * name from the stamp is carried only as free text for the vet to cross-
 * check against, never auto-linked to an identity.
 */
export const VACCINATION_SCAN_FIELD_NAMES = [
    "vaccineType",
    "dateAdministered",
    "batchSerial",
    "administeringVetName",
    "nextDue",
];
/** Fields the confirm screen must block save on until present — vaccine type
 * and the date administered are the two facts that make a dose a dose;
 * batch/serial, the OCR'd vet name, and a stamp-provided next-due date are
 * all optional/advisory (`docs/products/stable.md`: "compulsory fields block
 * save until present, exactly like the horse-add scan flow"). */
export const VACCINATION_SCAN_COMPULSORY_FIELD_NAMES = [
    "vaccineType",
    "dateAdministered",
];
const NO_VALUE = {
    value: null,
    confidence: null,
};
const LOCAL_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
/**
 * Best-effort normalization of a provider guess into this field's stored
 * shape, mirroring `normalizeField` in `horse-scan-extraction.ts`. A guess
 * that cannot be confidently mapped is treated as not extracted (`null`)
 * rather than passed through as a value the confirm screen would need to
 * silently trust — `vaccineType` in particular is normalized against the
 * Stable's actually configured vaccine-type codes, never accepted as
 * arbitrary free text, so the confirm screen only ever pre-selects a type
 * the vet could also have picked manually.
 */
function normalizeField(field, raw, knownVaccineTypeCodes) {
    if (raw === undefined || raw.value === null)
        return null;
    const trimmed = raw.value.trim();
    if (trimmed.length === 0)
        return null;
    switch (field) {
        case "vaccineType": {
            const upper = trimmed.toUpperCase().replace(/[\s-]+/g, "_");
            return knownVaccineTypeCodes.has(upper) ? upper : null;
        }
        case "dateAdministered":
        case "nextDue":
            return LOCAL_DATE_PATTERN.test(trimmed) ? trimmed : null;
        default:
            return trimmed;
    }
}
/**
 * Builds the confirm-screen draft fields from a provider's raw guesses —
 * mirrors `buildHorseScanFields`. Needs the Stable's currently configured
 * vaccine-type codes to normalize `vaccineType` against, since that field is
 * "a configuration lookup value, not a fixed enum"
 * (`docs/products/stable.md`).
 */
export function buildVaccinationScanFields(raw, knownVaccineTypeCodes) {
    const result = {};
    for (const field of VACCINATION_SCAN_FIELD_NAMES) {
        const rawField = raw[field];
        const normalized = normalizeField(field, rawField, knownVaccineTypeCodes);
        result[field] =
            normalized === null
                ? NO_VALUE
                : { value: normalized, confidence: rawField?.confidence ?? null };
    }
    return result;
}
export function missingCompulsoryVaccinationScanFields(fields) {
    return VACCINATION_SCAN_COMPULSORY_FIELD_NAMES.filter((field) => fields[field].value === null);
}
